import React, { useCallback, useEffect, useRef } from 'react';
import type { DocNode } from '@atlaskit/adf-schema';
import type { TriggerProps } from '@atlaskit/popup';

import {
	RemixContainer,
	PAGE_REMIX_OPTIONS,
	remixOverlayMessages,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';

import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import type { DynamicUiSource } from './common-types.ts';
import { flattenDocContentForPageRemixFragment } from './flattenDocContentForPageRemixFragment.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

const constructPageDynamicUiSource = (adfDocument: DocNode): DynamicUiSource => ({
	contentType: 'page' as const,
	startLocalId: '',
	endLocalId: '',
	startIndex: 0,
	endIndex: -1,
	fragmentAdf: flattenDocContentForPageRemixFragment(adfDocument),
	fullAdf: adfDocument,
});

export type PageRemixModalProps = {
	adfDocument: DocNode;
	interactionSource?: InteractionSource;
	onClose: () => void;
	onStartThinking: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	isEphemeralViewVisible: boolean;
	/** Render function for the trigger element that the popup anchors to. */
	trigger: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Defaults to 'left'. */
	popupPlacement?: 'left-end';
	/** Popup offset from the trigger in [along, away] px. */
	popupOffset?: [number, number];
	/** Whether the popup is open. */
	isPopupOpen?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
};

export const PageRemixModal = ({
	adfDocument,
	interactionSource,
	onClose,
	onStartThinking,
	onStopThinking,
	isEphemeralViewVisible,
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen,
	popupRepositionKey,
}: PageRemixModalProps) => {
	const isRemixCancelled = useRef(false);
	const openAIFCStagingModalRef = useRef<() => void>(() => {});

	const onActionReady = useCallback(() => {
		if (!isRemixCancelled.current) {
			openAIFCStagingModalRef.current();
			onStopThinking();
			onClose();
		}
	}, [onClose, onStopThinking]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		openAIFCStagingModal,
		closeAIFCStagingModal,
	} = useRemixToAIFC({ onActionReady });
	openAIFCStagingModalRef.current = openAIFCStagingModal;
	const { toggleConversationAssistant } = useToggleConversationAssistant();

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				onStartThinking();
			} else {
				isRemixCancelled.current = true;
				stopRemixing();
				onStopThinking({ reopenModal: true });
			}
		},
		[onStartThinking, stopRemixing, onStopThinking],
	);

	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			onStopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, onStopThinking]);

	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onStopThinking();
			onClose();
			toggleConversationAssistant('sidebar');
		}
	}, [
		isRemixError,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		toggleConversationAssistant,
	]);

	const handleOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			const dynamicUiSource = constructPageDynamicUiSource(adfDocument);
			await sendAIFCPrompt({
				userPrompt,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource,
			});
		},
		[sendAIFCPrompt, adfDocument, interactionSource],
	);

	return (
		<RemixContainer
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
			onRemixFlowActiveChange={onRemixFlowActiveChange}
			isEphemeralViewVisible={isEphemeralViewVisible}
			options={PAGE_REMIX_OPTIONS}
			subtitle={remixOverlayMessages.pageRemixSubtitle}
			presentation="popup"
			trigger={trigger}
			popupPlacement={popupPlacement}
			popupOffset={popupOffset}
			isPopupOpen={isPopupOpen}
			popupRepositionKey={popupRepositionKey}
		/>
	);
};
